/**
 * DraggableTopicCard — H.2 calendar drag source.
 *
 * Renders a single ContentTopic as a small card that the user can drag
 * onto a calendar slot. Uses native HTML5 drag-and-drop with a custom MIME
 * so DayCell can distinguish topic drops from chip drag-rescheduling.
 *
 * Pillars chip uses the topic's `primaryPillarId` for the colour cue —
 * groups in TopicBacklogSidebar are organised by `primaryPillarId`.
 */

import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { ContentTopic } from '../../types';
import { PROOF_OBJECT_HINTS, type ProofObjectHint } from '@segmently/schema-models/content-plan/constants';

export const TOPIC_DRAG_MIME = 'application/x-content-plan-topic-id';

/**
 * Wave 2 — guess the most-likely proof-object type the AI will need at
 * post-gen time, derived from `topic.dataUsage.items[].sourcePath`.
 * Heuristic only — falls back to `null` (no chip) when the source path
 * doesn't map to any known catalogue type.
 */
function inferProofType(topic: ContentTopic): ProofObjectHint | null {
  const items = (topic as any).dataUsage?.items as Array<{ sourcePath?: string }> | undefined;
  if (!items || items.length === 0) return null;

  // Prefer primary (sourcePriority === 1) if any.
  const primary =
    items.find((i) => ((i as any).sourcePriority ?? 9) === 1) ?? items[0];
  const path = primary?.sourcePath ?? '';
  if (!path) return null;

  const head = path.split('.')[0]?.toLowerCase() ?? '';
  // Known source-graph type prefixes → proof-catalogue mapping.
  if (head === 'reviews' || head === 'studycases' || head === 'study_cases') return 'customer_quote';
  if (head === 'bignumbers' || head === 'big_numbers' || head === 'ratings' || head === 'metrics') return 'metric';
  if (head === 'releases' || head === 'changelog') return 'changelog';
  if (head === 'events' || head === 'events_calendar') return 'event_link';
  if (head === 'screenshots' || head === 'gallery' || head === 'media') return 'screenshot';
  if (head === 'dashboards' || head === 'analytics_dashboard') return 'dashboard';
  // Fallback — anything else we don't classify (audiences, insights, tasks, …)
  // doesn't carry a proof-object affordance natively.
  return null;
}

export interface DraggableTopicCardProps {
  topic: ContentTopic;
  /** Optional callback invoked when drag starts — used to dim the source. */
  onDragStart?: (topicId: string) => void;
  onDragEnd?: () => void;
  /** Display the colour chip from this primary pillar. */
  primaryPillarColor?: string;
  /** Display name for the primary pillar (label on the chip). */
  primaryPillarName?: string;
  disabled?: boolean;
}

const TESTID_PREFIX = 'draggable-topic-card';

export const DraggableTopicCard: React.FC<DraggableTopicCardProps> = ({
  topic,
  onDragStart,
  onDragEnd,
  primaryPillarColor,
  primaryPillarName,
  disabled = false,
}) => {
  const handleDragStart = (e: React.DragEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(TOPIC_DRAG_MIME, topic.id);
    // text/plain fallback for browsers that don't preserve the custom MIME —
    // value is `topic:${id}` so DayCell.handleDrop can ignore it (it only
    // fires the topic path on the custom MIME read).
    e.dataTransfer.setData('text/plain', `topic:${topic.id}`);
    e.dataTransfer.effectAllowed = 'copyMove';
    onDragStart?.(topic.id);
  };

  return (
    <Box
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDragEnd={() => onDragEnd?.()}
      data-testid={`${TESTID_PREFIX}-${topic.id}`}
      sx={{
        p: 1,
        mb: 0.75,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        cursor: disabled ? 'default' : 'grab',
        opacity: disabled ? 0.8 : 1,
        '&:active': disabled ? undefined : { cursor: 'grabbing' },
        '&:hover': disabled ? undefined : { bgcolor: 'action.hover', borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              mb: 0.5,
            }}
          >
            {topic.angle ?? '(untitled topic)'}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {primaryPillarName && (
              <Chip
                size="small"
                label={primaryPillarName}
                sx={{
                  fontSize: '0.65rem',
                  height: 18,
                  bgcolor: primaryPillarColor ?? 'action.hover',
                  color: primaryPillarColor ? '#fff' : 'text.primary',
                }}
              />
            )}
            {/* Wave 2 — proof-object affordance chip. Surfaces what evidence
                the AI will need at post-gen time so the user can pre-assemble
                proofs. PROOF_OBJECT_HINTS const is the canonical list (also
                used in PromptInputResolver injection). */}
            {(() => {
              const proof = inferProofType(topic);
              if (!proof) return null;
              return (
                <Chip
                  size="small"
                  label={`📌 ${proof.replace('_', ' ')}`}
                  sx={{
                    fontSize: '0.6rem',
                    height: 18,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                  }}
                />
              );
            })()}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

// Re-export so consumers can render proof chips with the same heuristic
// elsewhere if needed (e.g. TopicDetailPanel, BacklogTable).
export { inferProofType };
export const PROOF_OBJECT_LABELS: Record<ProofObjectHint, string> = {
  metric: 'metric',
  screenshot: 'screenshot',
  dashboard: 'dashboard',
  customer_quote: 'quote',
  changelog: 'changelog',
  event_link: 'event',
};
// Reference the imported const so unused-import linting doesn't strip it
// when a downstream file consumes only the re-export.
export { PROOF_OBJECT_HINTS };
